import React from 'react';
import { WxbButton } from '../Button/Button';
import { WxbEmpty } from '../Empty/Empty';
import { WxbIcon } from '../Icon/Icon';
import type { WxbIconName } from '../Icon/icons';
import { WxbSpinner } from '../Spinner/Spinner';
import './Result.css';

export type WxbResultStatus =
  | 'loading'
  | 'empty'
  | 'success'
  | 'warning'
  | 'error'
  | '403'
  | '404';

export interface WxbResultProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  status?: WxbResultStatus;
  title?: React.ReactNode;
  description?: React.ReactNode;
  actions?: React.ReactNode;
  size?: 'sm' | 'md' | 'lg';
}

export interface WxbPageStateProps extends WxbResultProps {
  fullHeight?: boolean;
  onRetry?: () => void;
  retryText?: React.ReactNode;
}

const RESULT_META: Record<Exclude<WxbResultStatus, 'loading' | 'empty'>, { icon: WxbIconName; tone: string; title: string }> = {
  success: { icon: 'released', tone: 'success', title: '操作成功' },
  warning: { icon: 'deviation', tone: 'warning', title: '需要关注' },
  error: { icon: 'rejected', tone: 'error', title: '加载失败' },
  '403': { icon: 'quarantine', tone: 'warning', title: '暂无权限' },
  '404': { icon: 'batch-record', tone: 'neutral', title: '内容不存在' },
};

const joinClasses = (...classes: Array<string | false | undefined>) =>
  classes.filter(Boolean).join(' ');

export const WxbResult: React.FC<WxbResultProps> = ({
  status = 'empty',
  title,
  description,
  actions,
  size = 'md',
  className = '',
  children,
  ...props
}) => {
  if (status === 'loading') {
    return (
      <div className={joinClasses('wxb-result', `wxb-result-${size}`, 'wxb-result-loading', className)} {...props}>
        <WxbSpinner size={size === 'sm' ? 24 : 32} tip={typeof title === 'string' ? title : undefined} />
        {title && typeof title !== 'string' && <div className="wxb-result-title">{title}</div>}
        {description && <div className="wxb-result-desc">{description}</div>}
        {actions && <div className="wxb-result-actions">{actions}</div>}
        {children}
      </div>
    );
  }

  if (status === 'empty') {
    return (
      <WxbEmpty
        className={joinClasses('wxb-result', `wxb-result-${size}`, 'wxb-result-empty', className)}
        description={(
          <>
            <div className="wxb-result-title">{title ?? '暂无数据'}</div>
            {description && <div className="wxb-result-desc">{description}</div>}
          </>
        )}
        action={actions}
        {...props}
      />
    );
  }

  const meta = RESULT_META[status];

  return (
    <div
      className={joinClasses(
        'wxb-result',
        `wxb-result-${size}`,
        `wxb-result-${meta.tone}`,
        className,
      )}
      {...props}
    >
      <div className="wxb-result-icon" aria-hidden="true">
        <WxbIcon name={meta.icon} size={size === 'lg' ? 34 : 28} />
      </div>
      <div className="wxb-result-title">{title ?? meta.title}</div>
      {description && <div className="wxb-result-desc">{description}</div>}
      {actions && <div className="wxb-result-actions">{actions}</div>}
      {children}
    </div>
  );
};

export const WxbPageState: React.FC<WxbPageStateProps> = ({
  fullHeight = false,
  onRetry,
  retryText = '重试',
  className = '',
  actions,
  status = 'empty',
  ...props
}) => (
  <WxbResult
    status={status}
    actions={actions ?? (
      status === 'error' && onRetry
        ? <WxbButton variant="secondary" size="sm" onClick={onRetry}>{retryText}</WxbButton>
        : undefined
    )}
    className={joinClasses('wxb-page-state', fullHeight && 'wxb-page-state-full', className)}
    {...props}
  />
);
